import clsx from 'clsx'
import Spinner from 'common/components/Spinner'
import {useFormik} from 'formik'
import {useState} from 'react'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router'
import {toast} from 'react-toastify'
import {forgotPassword} from 'setup/redux/Actions/SignIn/forgotPassword'
import * as Yup from 'yup'

const initialValues = {
  email: '',
  resend: false,
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email().min(3).max(50).label('Email').required(),
  resend: Yup.boolean().label('Resend'),
})

export function LegacyForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [alreadySent, setAlreadySent] = useState<boolean>(false)
  const dispatch = useDispatch()

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      const onSuccess = () => {
        setHasErrors(false)
        setLoading(false)
        toast.success('Forgot password email sent.')
      }

      const onFailure = (err: any) => {
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        const message =
          err?.response?.data?.message ?? err?.message ?? 'Error requesting password reset'
        if (message === 'An email has already been sent to your account.') setAlreadySent(true)
        toast.error(message)
      }

      dispatch(forgotPassword(values.email, values.resend, onSuccess, onFailure))
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>Forgot Password ?</h1>
        <div className='text-gray-400 fw-bold fs-4'>Enter your email to reset your password.</div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            Sorry, looks like there are some errors detected, please try again.
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Sent password reset. Please check your email</div>
        </div>
      )}

      <div className='fv-row mb-10'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched.email && formik.errors.email,
          })}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='invalid-feedback'>{formik.errors.email}</div>
        )}
      </div>

      {alreadySent && (
        <div className='form-check form-switch mb-10'>
          <label className='form-check-label fw-bolder text-gray-900 fs-6'>Resend Email</label>
          <input
            type='checkbox'
            {...formik.getFieldProps('resend')}
            className={clsx('form-check-input', {
              'is-invalid': formik.touched.resend && formik.errors.resend,
            })}
          />
          {formik.touched.resend && formik.errors.resend && (
            <div className='invalid-feedback'>{formik.errors.resend}</div>
          )}
        </div>
      )}

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-lg btn-primary fw-bolder me-4'
          disabled={loading}
        >
          {loading && <Spinner size='sm' type='grow' className='me-1' />}
          <span className='indicator-label'>Submit</span>
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-lg btn-light-primary fw-bolder'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancel
          </button>
        </Link>
      </div>
    </form>
  )
}
